import { useEffect, useMemo, useState } from 'react';

import { CheckmarkCircle04Icon } from '@hugeicons-pro/core-stroke-rounded';
import { Delete01Icon, Delete02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { errorRouter } from 'helpers/error-router';
import { getRenewalStatus } from 'helpers/renewals-helper';
import { useTracking } from 'hooks/analytic-tracking';
import { useDocumentTitle } from 'hooks/document-title';
import { useLocale } from 'hooks/locale';
import { useToast } from 'hooks/toast';
import AppPage from 'layouts/AppPage/AppPage';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import Alert from 'components/Alert/Alert';
import { Select } from 'components/FormComponents';
import StatusPill from 'components/Pills/StatusPills';
import { TabSelect } from 'components/Tabs/Tabs';

import { useLazyGetBrandQuery } from 'api/brands-api';
import { getBrands, selectBrands } from 'api/brands-slice';
import { getCustomFields, selectEmployeeCustomFields } from 'api/candidate-custom-fields-slice';
import {
  useAddCandidateHistoryMutation,
  useSendCandidateRequestEmailMutation,
} from 'api/candidates-api';
import { deleteCandidate, getCandidate, updateCandidate } from 'api/candidates-slice';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import {
  useDeleteEmployeeMutation,
  useGetEmployeeQuery,
  useUpdateEmployeeMutation,
} from 'api/employees-api';
import { getTags } from 'api/tags-slice';
import { useGetCompanyUsersQuery, useGetUserTeamsQuery } from 'api/user-api';
import { selectUser } from 'api/user-slice';

import styles from './Employee.module.scss';
import { EmployeeDetails } from './tabs/EmployeeDetails/EmployeeDetails';
import { History } from './tabs/History/History';
import { Renewals } from './tabs/Renewals/Renewals';

export const Employee = () => {
  const { t } = useTranslation();

  const { id, tab, target } = useParams();

  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const companyChecks = useSelector(selectCompanyChecks);
  const customFields = useSelector(selectEmployeeCustomFields);
  const brands = useSelector(selectBrands);

  const { trackEvent } = useTracking(user);
  const showToast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: employeeData } = useGetEmployeeQuery(id, {
    refetchOnMountOrArgChange: true,
  });

  const [getBrand] = useLazyGetBrandQuery();

  const [sendCandidateRequestEmail] = useSendCandidateRequestEmailMutation();
  const [addCandidateHistory] = useAddCandidateHistoryMutation();
  const [updateEmployee] = useUpdateEmployeeMutation();
  const [deleteEmployee] = useDeleteEmployeeMutation();

  const [localLoading, setLocalLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState();
  const [candidateId, setCandidateId] = useState();
  const [candidate, setCandidate] = useState();
  const [tabKey, setTabKey] = useState('renewals');

  const [brand, setBrand] = useState();
  const [alertDelete, setAlertDelete] = useState(false);
  const [alertDeleteEmployee, setAlertDeleteEmployee] = useState(false);

  useDocumentTitle(candidate ? candidate.name : 'Loading...');

  const { dateSlashFormat } = useLocale(company?.locale);
  const { data: teams } = useGetUserTeamsQuery(user.id);

  const renewalStatus = useMemo(() => getRenewalStatus(candidate?.status), [candidate?.status]);

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname,
      title: 'Candidate check page',
    });
  }, []);

  useEffect(() => {
    if (!employeeData) return;

    setEmployee(employeeData);
    setCandidateId(employeeData.candidate_id);
  }, [employeeData]);

  // Load candidate
  useEffect(() => {
    if (!candidateId) return;

    const loadData = async () => {
      try {
        setLocalLoading(true);
        await Promise.all([
          dispatch(getTags()),
          dispatch(getCustomFields()),
          dispatch(getBrands()),
        ]);

        const { payload: candidate } = await dispatch(getCandidate(candidateId));
        setCandidate(candidate);

        const brand = await getBrand(candidate.brand_id).unwrap();
        setBrand(brand);
      } catch (error) {
        navigate(errorRouter(error));
      } finally {
        setLocalLoading(false);
      }
    };

    loadData();
  }, [candidateId]);

  const setTabPath = key => {
    setTabKey(key);
    navigate(`/employee/${id}/${key}`);
  };

  useEffect(() => {
    if (!tab) return;

    setTabKey(tab);
  }, [tab]);

  // TEMPLATE

  const onCheckDelete = async () => {
    setAlertDelete(false);
    setLocalLoading(true);
    await dispatch(deleteCandidate(id));
    showToast('Success', 'Candidate deleted', 'success');
    trackEvent('Candidate Deleted', { candidateId: id });
    setLocalLoading(false);
    navigate('/dashboard');
  };

  const hanldeSendCandidateRequestEmail = async () => {
    setLocalLoading(true);
    await sendCandidateRequestEmail(id);
    showToast('Success', 'New candidate request sent', 'success');
    setLocalLoading(false);
  };

  const handleUpdateCandidate = async params => {
    setLocalLoading(true);
    const { payload: candidate } = await dispatch(updateCandidate({ id: candidateId, params }));
    setCandidate(candidate);
    setLocalLoading(false);
  };

  const handleUpdateEmployee = async data => {
    setLocalLoading(true);
    const { data: updatedEmployee } = await updateEmployee({ id: employee.id, data });
    setEmployee(updatedEmployee);
    setLocalLoading(false);
  };

  const handleDeleteEmployee = async () => {
    setAlertDeleteEmployee(false);
    setLocalLoading(true);
    await deleteEmployee(employee.id);
    showToast('Success', 'Employee deleted', 'success');
    setLocalLoading(false);
    navigate('/renewals');
  };

  const { data: companyUsers } = useGetCompanyUsersQuery(user?.id, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <AppPage loading={loading || localLoading}>
      <Alert
        show={alertDelete}
        title={t('check.deleteCandidateTitle')}
        message={t('check.deleteCandidateMessage')}
        ok="Delete"
        onOk={params => {
          setAlertDelete(false);
          onCheckDelete(params);
        }}
        cancel="Cancel"
        type="error"
        showConfirmationInput
        icon={Delete02Icon}
        onCancel={() => setAlertDelete(false)}
      />
      <Alert
        show={alertDeleteEmployee}
        title="Delete Employee"
        message="Are you sure you want to delete this employee? This will also mark the associated candidate as deleted."
        ok="Delete"
        onOk={() => {
          handleDeleteEmployee();
        }}
        cancel="Cancel"
        type="error"
        showConfirmationInput
        icon={Delete01Icon}
        onCancel={() => setAlertDeleteEmployee(false)}
      />
      {candidate && (
        <div className={styles.root}>
          <div className={styles.header}>
            <div>
              <div>
                <p className={'u-padding-right--small u-margin-bottom--small t-small'}>
                  <a className="u-link dark" onClick={() => navigate(-1)}>
                    BACK
                  </a>{' '}
                  /
                </p>
                <div className="d-flex justify-content-start align-items-center">
                  <h1 className="title-2">{candidate.name}</h1>
                  <Select
                    small
                    className={cn(styles.hiringStatusSelect, 'u-margin-left')}
                    rounded
                    value={employee?.active}
                    onChange={e => handleUpdateEmployee({ active: e.target.value })}>
                    <Select.Item value={true}>
                      <div className="d-flex align-items-center">
                        <HugeiconsIcon
                          size="18"
                          icon={CheckmarkCircle04Icon}
                          strokeWidth="2"
                          className={cn('text-success', 'u-margin-right--small')}
                        />{' '}
                        Active
                      </div>
                    </Select.Item>
                    <Select.Item value={false}>
                      <div className="d-flex align-items-center">
                        <HugeiconsIcon
                          size="18"
                          icon={CheckmarkCircle04Icon}
                          strokeWidth="2"
                          className={cn('text-error', 'u-margin-right--small')}
                        />{' '}
                        Inactive
                      </div>
                    </Select.Item>
                  </Select>
                  <ActionsMenu newIconLibrary className="u-margin-left--small">
                    <ActionsMenu.Item
                      icon={Delete01Icon}
                      title="Delete Employee"
                      type="error"
                      newIconLibrary
                      onClick={() => setAlertDeleteEmployee(true)}
                    />
                  </ActionsMenu>
                </div>
              </div>
            </div>
            <div
              className={cn(
                styles.employeeDetails,
                'd-flex',
                'justify-content-start',
                'align-items-start',
                'u-padding-top',
                'u-width-100',
              )}>
              <div className="d-flex justify-content-start align-items-center">
                <StatusPill status={candidate.status} text={renewalStatus} large capitalize />
              </div>
              <div className={cn(styles.infoFieldRow)}>
                {candidate.reference_code && (
                  <div className={styles.infoField}>
                    <p className={styles.title}>{t('employee.infoFields.referenceCode')}</p>
                    <p className={styles.value}>{candidate.reference_code}</p>
                  </div>
                )}
                <div className={styles.infoField}>
                  <p className={styles.title}>{t('employee.infoFields.role')}</p>
                  <p className={styles.value}>{candidate.job_role}</p>
                </div>
                <div className={styles.infoField}>
                  <p className={styles.title}>{t('employee.infoFields.department')}</p>
                  <p className={styles.value}>{candidate.department}</p>
                </div>
                <div className={styles.infoField}>
                  <p className={styles.title}>{t('employee.infoFields.employedOn')}</p>
                  <p className={styles.value}>
                    <Moment format={`${dateSlashFormat} @hh:mma`}>{employee.created_at}</Moment>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <TabSelect
            id="accountTabs"
            onSelect={setTabPath}
            activeKey={tab}
            defaultActiveKey="renewals"
            newTabKey={tabKey}
            className="tabs"
            mountOnEnter={true}
            unmountOnExit={true}>
            <TabSelect.Tab eventKey="renewals" title={t('employee.tabs.renewals')}>
              <Renewals
                target={target}
                company={company}
                employee={employee}
                candidate={candidate}
                user={user}
                setEmployee={setEmployee}
                setLoading={setLoading}
                handleUpdateEmployee={handleUpdateEmployee}
              />
            </TabSelect.Tab>
            <TabSelect.Tab eventKey="details" title={t('employee.tabs.employee_details.title')}>
              <EmployeeDetails
                company={company}
                candidate={candidate}
                brands={brands}
                user={user}
                teams={teams}
                companyUsers={companyUsers}
                customFields={customFields}
                handleCandidateUpdate={handleUpdateCandidate}
                sendRequestEmail={hanldeSendCandidateRequestEmail}
                brand={brand}
              />
            </TabSelect.Tab>
            <TabSelect.Tab eventKey="history" title={t('check.tabs.history')}>
              <History
                employee={employee}
                company={company}
                candidate={candidate}
                user={user}
                companyChecks={companyChecks}
                setLoading={setLoading}
                targetNewRecord={target === 'new'}
                onCandidateRequest={hanldeSendCandidateRequestEmail}
                createRecord={addCandidateHistory}
              />
            </TabSelect.Tab>
          </TabSelect>
        </div>
      )}
    </AppPage>
  );
};
